/**
 * API сервисы для работы с backend
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  LoginRequest,
  RegisterRequest,
  PasswordResetRequest,
  PasswordResetConfirm,
  AuthResponse,
  Trainer, 
  TrainerUpdate,
  Client, 
  ClientCreate, 
  ClientUpdate, 
  MuscleGroup,
  MuscleGroupCreate,
  Exercise,
  ExerciseCreate,
  ExerciseUpdate,
  Workout,
  WorkoutCreate,
  WorkoutUpdate,
  WorkoutExercise,
  WorkoutExerciseCreate,
  WorkoutExerciseUpdate,
  ExerciseLog,
  ExerciseLogCreate,
  ExerciseLogUpdate,
  WorkoutFilters,
  ExerciseFilters,
  PaginationParams,
  ApiError,
  ActionLog,
  LogsStats,
  LogsFilters,
  ClientWeight, 
  ClientWeightCreate,
  BodyMeasurement,
  BodyMeasurementCreate,
  WorkoutData,
  ExerciseData,
  ClientExerciseException,
  ClientExerciseExceptionCreate,
  ClientExerciseExceptionUpdate,
  ExerciseExceptionCheck,
  ExerciseTemplate,
  ExerciseTemplateCreate,
  ExerciseTemplateUpdate,
  ExerciseTemplateExercise,
  ExerciseTemplateExerciseCreate,
  ExerciseTemplateExerciseUpdate,
  ClientExercise,
  ExerciseProgress,
  BestProgressExercise,
  LogsCleanupSettings,
  LogsCleanupSettingsUpdate,
  PaginatedResponse
} from '../types';

// Базовая конфигурация axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// API Configuration
// console.log('🔧 API Configuration:');
// console.log('  REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
// console.log('  API_BASE_URL:', API_BASE_URL);

class ApiService {
  private client: AxiosInstance;
  private refreshPromise: Promise<AuthResponse> | null = null;
  private maxRetries = 3;
  private retryDelay = 1000; // начальная задержка в мс

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // console.log('🔧 Axios baseURL:', this.client.defaults.baseURL);

    // Добавляем interceptor для автоматического добавления токена
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('trainer_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Добавляем interceptor для обработки ошибок и автоматического обновления токена
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Проверяем, что это 401 ошибка и не повторный запрос
        if (status === 401 && !originalRequest._retry) {
          // Пропускаем обновление для запросов аутентификации
          const isAuthRequest =
            originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/register') ||
            originalRequest?.url?.includes('/auth/forgot-password') ||
            originalRequest?.url?.includes('/auth/reset-password') ||
            originalRequest?.url?.includes('/auth/refresh');
          
          if (isAuthRequest) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            // Используем централизованный refresh
            await this.refreshTokenWithRetry();
            
            // Повторяем оригинальный запрос с новым токеном
            const newToken = localStorage.getItem('trainer_token');
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh не удался, перенаправляем на логин
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Централизованный метод для обновления токена с retry логикой
  private async refreshTokenWithRetry(): Promise<AuthResponse> {
    // Если уже идет процесс обновления, ждем его результата
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Создаем новый процесс обновления
    this.refreshPromise = this.performRefreshWithRetry()
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async performRefreshWithRetry(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('trainer_refresh_token');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Проверяем валидность refresh токена
    if (!this.isTokenValid(refreshToken)) {
      throw new Error('Refresh token expired');
    }

    let lastError: any;
    let delay = this.retryDelay;

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        // Создаем отдельный axios instance для refresh запроса
        const refreshClient = axios.create({
          baseURL: `${API_BASE_URL}/api`,
          headers: { 'Content-Type': 'application/json' }
        });

        const response = await refreshClient.post<AuthResponse>('/auth/refresh', {
          refresh_token: refreshToken
        });

        const data = response.data;
        
        // Сохраняем новые токены
        localStorage.setItem('trainer_token', data.access_token);
        localStorage.setItem('trainer_refresh_token', data.refresh_token);
        
        return data;
      } catch (error) {
        lastError = error;
        
        // Если это последняя попытка или критическая ошибка, прерываем
        if (attempt === this.maxRetries - 1 || 
            (error as any)?.response?.status === 403) {
          break;
        }
        
        // Экспоненциальная задержка перед следующей попыткой
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Удваиваем задержку для следующей попытки
      }
    }

    throw lastError || new Error('Token refresh failed after retries');
  }

  // Проверка валидности токена
  private isTokenValid(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      
      // Проверяем, что токен еще не истек
      return payload.exp && payload.exp > now;
    } catch {
      return false;
    }
  }

  // Обработка полной неудачи аутентификации
  private handleAuthFailure(): void {
    localStorage.removeItem('trainer_token');
    localStorage.removeItem('trainer_refresh_token');
    
    // Проверяем, что мы не на странице логина, чтобы избежать бесконечного редиректа
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  // Утилитарные методы
  private handleResponse<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  private handleError(error: any): never {
    let errorMessage = 'Network error';
    
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Если это ошибка валидации FastAPI (массив объектов с полями type, loc, msg)
      if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
        const validationErrors = errorData.detail
          .map((err: any) => {
            const field = Array.isArray(err.loc) ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          })
          .join(', ');
        errorMessage = validationErrors;
      } 
      // Если это обычная ошибка с detail как строкой
      else if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      }
      // Если есть message
      else if (errorData.message) {
        errorMessage = errorData.message;
      }
    }
    
    const apiError: ApiError = {
      detail: errorMessage,
      status_code: error.response?.status || 500
    };
    throw apiError;
  }

  // Методы аутентификации
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.client.post<AuthResponse>('/auth/login', credentials);
      const data = this.handleResponse(response);
      
      // Сохраняем токены в localStorage
      localStorage.setItem('trainer_token', data.access_token);
      localStorage.setItem('trainer_refresh_token', data.refresh_token);
      
      return data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async register(userData: RegisterRequest): Promise<Trainer> {
    try {
      const response = await this.client.post<Trainer>('/auth/register', userData);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      // Используем централизованный метод обновления с retry
      return await this.refreshTokenWithRetry();
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getProfile(): Promise<Trainer> {
    try {
      const response = await this.client.get<Trainer>('/auth/me');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } catch (error) {
      // Игнорируем ошибки при logout
    } finally {
      localStorage.removeItem('trainer_token');
      localStorage.removeItem('trainer_refresh_token');
    }
  }

  async updateProfile(data: TrainerUpdate): Promise<Trainer> {
    try {
      const response = await this.client.put<Trainer>('/auth/profile', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async changePassword(data: { current_password: string; new_password: string }): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/auth/change-password', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async forgotPassword(data: PasswordResetRequest): Promise<{ message: string }> {
    try {
      console.log('API: Отправляем запрос forgotPassword:', data);
      console.log('API: URL запроса:', this.client.defaults.baseURL + '/auth/forgot-password');
      console.log('API: Headers:', this.client.defaults.headers);
      console.log('API: Начинаем выполнение POST запроса...');
      const response = await this.client.post<{ message: string }>('/auth/forgot-password', data);
      console.log('API: POST запрос выполнен успешно');
      console.log('API: Получен ответ:', response.data);
      return this.handleResponse(response);
    } catch (error: any) {
      console.error('API: Ошибка в forgotPassword:', error);
      console.error('API: Тип ошибки:', typeof error);
      console.error('API: Сообщение ошибки:', error.message);
      console.error('API: Детали ошибки:', error.response?.data);
      console.error('API: Статус ошибки:', error.response?.status);
      console.error('API: Заголовки ответа:', error.response?.headers);
      return this.handleError(error);
    }
  }

  async resetPassword(data: PasswordResetConfirm): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/auth/reset-password', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с тренером (дублируются методы из auth секции выше)

  async deleteProfile(): Promise<void> {
    try {
      await this.client.delete('/trainers/profile');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с клиентами
  async getClients(params: PaginationParams = {}): Promise<Client[]> {
    try {
      const response = await this.client.get<Client[]>('/clients/', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClient(id: number): Promise<Client> {
    try {
      const response = await this.client.get<Client>(`/clients/${id}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createClient(data: ClientCreate): Promise<Client> {
    try {
      const response = await this.client.post<Client>('/clients/', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateClient(id: number, data: ClientUpdate): Promise<Client> {
    try {
      const response = await this.client.put<Client>(`/clients/${id}`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteClient(id: number): Promise<void> {
    try {
      await this.client.delete(`/clients/${id}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClientWeights(clientId: number): Promise<ClientWeight[]> {
    const response = await this.client.get(`/clients/${clientId}/weights`);
    return this.handleResponse(response);
  }

  async addClientWeight(clientId: number, data: ClientWeightCreate): Promise<ClientWeight> {
    const response = await this.client.post(`/clients/${clientId}/weights`, data);
    return this.handleResponse(response);
  }

  async deleteClientWeight(clientId: number, weightId: number): Promise<void> {
    try {
      await this.client.delete(`/clients/${clientId}/weights/${weightId}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClientBodyMeasurements(clientId: number) {
    const response = await this.client.get(`/clients/${clientId}/body-measurements`);
    return this.handleResponse(response);
  }

  async addClientBodyMeasurement(clientId: number, data: BodyMeasurementCreate) {
    const response = await this.client.post(`/clients/${clientId}/body-measurements`, data);
    return this.handleResponse(response);
  }

  async deleteClientBodyMeasurement(clientId: number, measurementId: number): Promise<void> {
    try {
      await this.client.delete(`/clients/${clientId}/body-measurements/${measurementId}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClientWorkoutsStats(clientId: number): Promise<WorkoutData[]> {
    const response = await this.client.get(`/clients/${clientId}/workouts-stats`);
    return this.handleResponse(response);
  }

  async getClientExercisesStats(clientId: number): Promise<ExerciseData[]> {
    const response = await this.client.get(`/clients/${clientId}/exercises-stats`);
    return this.handleResponse(response);
  }

  async getClientExercisesWithWeight(clientId: number): Promise<ClientExercise[]> {
    const response = await this.client.get(`/clients/${clientId}/exercises-with-weight`);
    return this.handleResponse(response);
  }

  async getClientExerciseProgress(clientId: number, exerciseId: number): Promise<ExerciseProgress[]> {
    const response = await this.client.get(`/clients/${clientId}/exercises/${exerciseId}/progress`);
    return this.handleResponse(response);
  }

  async getClientBestProgressExercise(clientId: number): Promise<BestProgressExercise> {
    const response = await this.client.get(`/clients/${clientId}/best-progress-exercise`);
    return this.handleResponse(response);
  }

  // Методы для работы с группами мышц
  async getMuscleGroups(params: PaginationParams = {}): Promise<MuscleGroup[]> {
    try {
      const response = await this.client.get<MuscleGroup[]>('/muscle-groups/', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getMuscleGroup(id: number): Promise<MuscleGroup> {
    try {
      const response = await this.client.get<MuscleGroup>(`/muscle-groups/${id}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createMuscleGroup(data: MuscleGroupCreate): Promise<MuscleGroup> {
    try {
      const response = await this.client.post<MuscleGroup>('/muscle-groups/', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с упражнениями
  async getExercises(params: ExerciseFilters = {}): Promise<Exercise[]> {
    try {
      const response = await this.client.get<Exercise[]>('/exercises/', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getExercisesCount(params: Omit<ExerciseFilters, 'skip' | 'limit'> = {}): Promise<{ total: number }> {
    try {
      const response = await this.client.get<{ total: number }>('/exercises/count', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getExercise(id: number): Promise<Exercise> {
    try {
      const response = await this.client.get<Exercise>(`/exercises/${id}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createExercise(data: ExerciseCreate): Promise<Exercise> {
    try {
      const response = await this.client.post<Exercise>('/exercises/', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateExercise(id: number, data: ExerciseUpdate): Promise<Exercise> {
    try {
      const response = await this.client.put<Exercise>(`/exercises/${id}`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteExercise(id: number): Promise<void> {
    try {
      await this.client.delete(`/exercises/${id}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с тренировками
  async getWorkouts(params: WorkoutFilters = {}): Promise<Workout[]> {
    try {
      const response = await this.client.get<Workout[]>('/workouts/', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getWorkoutsPaginated(params: WorkoutFilters = {}): Promise<PaginatedResponse<Workout>> {
    try {
      const response = await this.client.get<PaginatedResponse<Workout>>('/workouts/paginated', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getWorkoutsCount(params: Omit<WorkoutFilters, 'skip' | 'limit'> = {}): Promise<{ total: number }> {
    try {
      const response = await this.client.get<{ total: number }>('/workouts/count', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getWorkout(id: number): Promise<Workout> {
    try {
      const response = await this.client.get<Workout>(`/workouts/${id}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createWorkout(data: WorkoutCreate): Promise<Workout> {
    try {
      const response = await this.client.post<Workout>('/workouts/', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateWorkout(id: number, data: WorkoutUpdate): Promise<Workout> {
    try {
      const response = await this.client.put<Workout>(`/workouts/${id}`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteWorkout(id: number): Promise<void> {
    try {
      await this.client.delete(`/workouts/${id}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async removeClientFromWorkout(workoutId: number, clientId: number): Promise<{
    message: string;
    deleted_logs_count: number;
    deleted_exercises_count: number;
    remaining_clients_count: number;
  }> {
    try {
      const response = await this.client.delete(`/workouts/${workoutId}/clients/${clientId}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async cleanupDeletedExercises(workoutId: number): Promise<{ message: string; deleted_count: number }> {
    try {
      const response = await this.client.post<{ message: string; deleted_count: number }>(
        `/workouts/${workoutId}/cleanup-deleted-exercises`
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с упражнениями в тренировке
  async addExerciseToWorkout(workoutId: number, data: WorkoutExerciseCreate): Promise<WorkoutExercise> {
    try {
      const response = await this.client.post<WorkoutExercise>(
        `/workouts/${workoutId}/exercises`,
        data
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateWorkoutExercise(workoutId: number, exerciseId: number, data: WorkoutExerciseUpdate): Promise<WorkoutExercise> {
    try {
      const response = await this.client.put<WorkoutExercise>(
        `/workouts/${workoutId}/exercises/${exerciseId}`,
        data
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteWorkoutExercise(workoutId: number, exerciseId: number): Promise<void> {
    try {
      await this.client.delete(`/workouts/${workoutId}/exercises/${exerciseId}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с логами упражнений
  async createExerciseLog(workoutId: number, exerciseId: number, data: ExerciseLogCreate): Promise<ExerciseLog> {
    try {
      const response = await this.client.post<ExerciseLog>(
        `/workouts/${workoutId}/exercises/${exerciseId}/logs`,
        data
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async copyExercisesBetweenClients(
    workoutId: number, 
    sourceClientId: number, 
    targetClientIds: number[]
  ): Promise<{ message: string; copied_count: number; target_clients_count: number }> {
    try {
      const params = new URLSearchParams();
      params.append('source_client_id', sourceClientId.toString());
      targetClientIds.forEach(id => params.append('target_client_ids', id.toString()));
      
      const response = await this.client.post(
        `/workouts/${workoutId}/copy-exercises?${params.toString()}`
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClientExerciseLogs(clientId: number, params: PaginationParams = {}): Promise<ExerciseLog[]> {
    try {
      const response = await this.client.get<ExerciseLog[]>(`/workouts/logs/${clientId}`, { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClientExerciseLogsForStats(clientId: number, params: PaginationParams = {}): Promise<ExerciseLog[]> {
    try {
      const response = await this.client.get<ExerciseLog[]>(`/workouts/logs/${clientId}/stats`, { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getClientPastWorkouts(clientId: number, params: PaginationParams = {}): Promise<Workout[]> {
    try {
      const response = await this.client.get<Workout[]>(`/clients/${clientId}/past-workouts`, { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateExerciseLog(logId: number, data: ExerciseLogUpdate): Promise<ExerciseLog> {
    try {
      const response = await this.client.put<ExerciseLog>(`/workouts/logs/${logId}`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteExerciseLog(logId: number): Promise<{ message: string }> {
    try {
      const response = await this.client.delete(`/workouts/logs/${logId}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для загрузки файлов
  uploadImage = async (file: File): Promise<{ file_url: string; filename: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await this.client.post('/uploads/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return this.handleResponse(response);
    } catch (error: any) {
      console.error('Ошибка загрузки изображения:', error);
      const apiError: ApiError = {
        detail: error.response?.data?.detail || 'Ошибка при загрузке изображения',
        status_code: error.response?.status || 500
      };
      throw apiError;
    }
  }

  uploadVideo = async (file: File): Promise<{ file_url: string; filename: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await this.client.post('/uploads/video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return this.handleResponse(response);
    } catch (error: any) {
      console.error('Ошибка загрузки видео:', error);
      const apiError: ApiError = {
        detail: error.response?.data?.detail || 'Ошибка при загрузке видео',
        status_code: error.response?.status || 500
      };
      throw apiError;
    }
  }

  // Утилитарные методы
  isAuthenticated(): boolean {
    const token = localStorage.getItem('trainer_token');
    if (!token) return false;
    
    // Используем централизованную проверку валидности
    const isValid = this.isTokenValid(token);
    
    if (!isValid) {
      // Проверяем, есть ли refresh token для возможного обновления
      const refreshToken = localStorage.getItem('trainer_refresh_token');
      if (refreshToken && this.isTokenValid(refreshToken)) {
        // Refresh token валиден, токен может быть обновлен
        return true;
      }
      
      // Оба токена невалидны, очищаем
      localStorage.removeItem('trainer_token');
      localStorage.removeItem('trainer_refresh_token');
      return false;
    }
    
    return true;
  }

  getAuthToken(): string | null {
    return localStorage.getItem('trainer_token');
  }

  // Методы для работы с настройками упражнений по умолчанию
  async getDefaultExerciseSettings(): Promise<{
    sets: number;
    reps: number;
    weight: number;
    rest_time: number;
  }> {
    try {
      const response = await this.client.get('/settings/default-exercise');
      return this.handleResponse(response);
    } catch (error) {
      // Если настройки не найдены, возвращаем значения по умолчанию
      return {
        sets: 3,
        reps: 10,
        weight: 0,
        rest_time: 90
      };
    }
  }

  async saveDefaultExerciseSettings(settings: {
    sets: number;
    reps: number;
    weight: number;
    rest_time: number;
  }): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/settings/default-exercise', settings);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Получение веса упражнения из истории тренировок
  async getExerciseWeightFromHistory(exerciseId: number, clientId?: number): Promise<number> {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await this.client.get(`/settings/exercise-weight-history/${exerciseId}`, { params });
      const data = this.handleResponse(response);
      return data.last_weight || 0;
    } catch (error) {
      // Если история не найдена, возвращаем 0
      return 0;
    }
  }

  // Методы для работы с логами действий (только для администраторов)
  async getActionLogs(filters: LogsFilters = {}): Promise<ActionLog[]> {
    try {
      const response = await this.client.get<ActionLog[]>('/logs', { params: filters });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getLogsStats(days: number = 30): Promise<LogsStats> {
    try {
      const response = await this.client.get<LogsStats>(`/logs/stats?days=${days}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getTrainersForLogs(): Promise<Array<{ id: number; name: string; email: string }>> {
    try {
      const response = await this.client.get<Array<{ id: number; name: string; email: string }>>('/logs/trainers');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getActionTypes(): Promise<string[]> {
    try {
      const response = await this.client.get<string[]>('/logs/action-types');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getResourceTypes(): Promise<string[]> {
    try {
      const response = await this.client.get<string[]>('/logs/resource-types');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Фото клиента ---
  async getClientLastPhoto(clientId: number) {
    const res = await this.client.get(`/clients/${clientId}/photos/last`);
    return res.data;
  }

  async getClientPrevLastPhoto(clientId: number) {
    const res = await this.client.get(`/clients/${clientId}/photos/prevlast`);
    return res.data;
  }

  async getClientPhotoDates(clientId: number): Promise<string[]> {
    const res = await this.client.get(`/clients/${clientId}/photos/dates`);
    return res.data;
  }

  async getClientPhotoById(clientId: number, photoId: number) {
    const res = await this.client.get(`/clients/${clientId}/photos/${photoId}`);
    return res.data;
  }

  async getClientPhotoCollage(clientId: number, date?: string): Promise<Blob> {
    const url = date
      ? `/clients/${clientId}/photos/collage?date=${date}`
      : `/clients/${clientId}/photos/collage`;
    const res = await this.client.get(url, { responseType: 'blob' });
    return res.data;
  }

  async getClientPhotosByDate(clientId: number, date: string) {
    const res = await this.client.get(`/clients/${clientId}/photos/by-date/${date}`);
    return res.data;
  }

  async uploadClientPhoto(clientId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await this.client.post(`/clients/${clientId}/photos/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  }

  async uploadClientPhotoWithView(clientId: number, file: File, view: string, date: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('view', view);
    formData.append('date', date);
    const res = await this.client.post(`/clients/${clientId}/photos/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  }

  // --- Исключения упражнений ---
  async getClientExceptions(clientId: number): Promise<ClientExerciseException[]> {
    try {
      const response = await this.client.get<ClientExerciseException[]>(`/clients/${clientId}/exceptions`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createClientException(clientId: number, data: ClientExerciseExceptionCreate): Promise<ClientExerciseException> {
    try {
      const response = await this.client.post<ClientExerciseException>(`/clients/${clientId}/exceptions`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateClientException(clientId: number, exceptionId: number, data: ClientExerciseExceptionUpdate): Promise<ClientExerciseException> {
    try {
      const response = await this.client.put<ClientExerciseException>(`/clients/${clientId}/exceptions/${exceptionId}`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteClientException(clientId: number, exceptionId: number): Promise<{ message: string }> {
    try {
      const response = await this.client.delete<{ message: string }>(`/clients/${clientId}/exceptions/${exceptionId}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async checkExerciseException(clientId: number, exerciseId: number): Promise<ExerciseExceptionCheck> {
    try {
      const response = await this.client.get<ExerciseExceptionCheck>(`/clients/${clientId}/exceptions/check/${exerciseId}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Шаблоны упражнений ---
  async getExerciseTemplates(params: PaginationParams = {}): Promise<ExerciseTemplate[]> {
    try {
      const response = await this.client.get<ExerciseTemplate[]>('/templates', { params });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getExerciseTemplate(id: number): Promise<ExerciseTemplate> {
    try {
      const response = await this.client.get<ExerciseTemplate>(`/templates/${id}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createExerciseTemplate(data: ExerciseTemplateCreate): Promise<ExerciseTemplate> {
    try {
      const response = await this.client.post<ExerciseTemplate>('/templates', data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateExerciseTemplate(id: number, data: ExerciseTemplateUpdate): Promise<ExerciseTemplate> {
    try {
      const response = await this.client.put<ExerciseTemplate>(`/templates/${id}`, data);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteExerciseTemplate(id: number): Promise<void> {
    try {
      await this.client.delete(`/templates/${id}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async addExerciseToTemplate(templateId: number, data: ExerciseTemplateExerciseCreate): Promise<ExerciseTemplateExercise> {
    try {
      const response = await this.client.post<ExerciseTemplateExercise>(
        `/templates/${templateId}/exercises`,
        data
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateExerciseTemplateExercise(templateId: number, exerciseId: number, data: ExerciseTemplateExerciseUpdate): Promise<ExerciseTemplateExercise> {
    try {
      const response = await this.client.put<ExerciseTemplateExercise>(
        `/templates/${templateId}/exercises/${exerciseId}`,
        data
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteExerciseTemplateExercise(templateId: number, exerciseId: number): Promise<void> {
    try {
      await this.client.delete(`/templates/${templateId}/exercises/${exerciseId}`);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async saveTemplateFromWorkoutExercises(
    workoutId: number, 
    clientId: number, 
    templateName: string, 
    description?: string
  ): Promise<ExerciseTemplate> {
    try {
      const response = await this.client.post<ExerciseTemplate>(
        `/workouts/${workoutId}/save-template`,
        {
          client_id: clientId,
          template_name: templateName,
          description: description
        }
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async importTemplateToWorkout(
    workoutId: number, 
    templateId: number,
    clientId: number
  ): Promise<{ message: string; added_count: number; skipped_count: number }> {
    try {
      const response = await this.client.post<{ message: string; added_count: number; skipped_count: number }>(
        `/workouts/${workoutId}/import-template/${templateId}?client_id=${clientId}`
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Управление пакетами в тренировках ---

  async getWorkoutPackageConsumption(workoutId: number): Promise<any[]> {
    try {
      const response = await this.client.get(`/workouts/${workoutId}/package-consumption`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async assignPackageToWorkoutClient(
    workoutId: number,
    clientId: number,
    clientPackageId: number
  ): Promise<{ message: string; consumption_id: number; package_name: string }> {
    try {
      const response = await this.client.post(
        `/workouts/${workoutId}/clients/${clientId}/assign-package`,
        { client_package_id: clientPackageId }
      );
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async removePackageFromWorkoutClient(
    workoutId: number,
    clientId: number
  ): Promise<{ message: string }> {
    try {
      const response = await this.client.delete(`/workouts/${workoutId}/clients/${clientId}/package`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Админка ---
  async getAdminTrainers(filters: {
    search?: string;
    is_active?: boolean;
    is_admin?: boolean;
    skip?: number;
    limit?: number;
  } = {}): Promise<Trainer[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.is_active !== undefined) {
        params.append('is_active', filters.is_active.toString());
      }
      if (filters.is_admin !== undefined) {
        params.append('is_admin', filters.is_admin.toString());
      }
      if (filters.skip !== undefined) {
        params.append('skip', filters.skip.toString());
      }
      if (filters.limit !== undefined) {
        params.append('limit', filters.limit.toString());
      }

      const response = await this.client.get<Trainer[]>(`/admin/trainers?${params.toString()}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getAdminTrainer(trainerId: number): Promise<Trainer> {
    try {
      const response = await this.client.get<Trainer>(`/admin/trainers/${trainerId}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async toggleAdminStatus(trainerId: number): Promise<{ message: string; trainer_id: number; is_admin: boolean }> {
    try {
      const response = await this.client.put<{ message: string; trainer_id: number; is_admin: boolean }>(`/admin/trainers/${trainerId}/toggle-admin`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async toggleActiveStatus(trainerId: number): Promise<{ message: string; trainer_id: number; is_active: boolean }> {
    try {
      const response = await this.client.put<{ message: string; trainer_id: number; is_active: boolean }>(`/admin/trainers/${trainerId}/toggle-active`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteTrainer(trainerId: number): Promise<{ message: string; trainer_id: number }> {
    try {
      const response = await this.client.delete<{ message: string; trainer_id: number }>(`/admin/trainers/${trainerId}`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async changeTrainerPassword(trainerId: number, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await this.client.put<{ message: string }>(`/admin/trainers/${trainerId}/change-password`, {
        new_password: newPassword
      });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getTrainerStats(trainerId: number): Promise<{
    trainer_id: number;
    trainer_name: string;
    email: string;
    is_active: boolean;
    is_admin: boolean;
    created_at: string;
    stats: {
      active_clients_count: number;
      exercises_count: number;
      workouts_count: number;
      action_logs_count: number;
    };
    recent_actions: Array<{
      action_type: string;
      resource_type: string;
      description: string;
      created_at: string;
    }>;
  }> {
    try {
      const response = await this.client.get(`/admin/trainers/${trainerId}/stats`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Функции для управления порядком упражнений клиентов
  async getClientExerciseOrder(workoutId: number, clientId: number): Promise<Array<{
    log_id: number;
    workout_exercise_id: number;
    exercise_id: number;
    exercise_name: string;
    order_index: number;
    sets: number;
    reps: number;
    weight?: number;
    is_completed: boolean;
  }>> {
    try {
      const response = await this.client.get(`/workouts/${workoutId}/clients/${clientId}/exercise-order`);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateClientExerciseOrder(
    workoutId: number, 
    clientId: number, 
    logId: number, 
    orderIndex: number
  ): Promise<ExerciseLog> {
    try {
      const response = await this.client.put(`/workouts/${workoutId}/clients/${clientId}/exercise-order/${logId}`, {
        order_index: orderIndex
      });
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateClientExerciseOrderBulk(
    workoutId: number, 
    clientId: number, 
    orderUpdates: Array<{ log_id: number; order_index: number }>
  ): Promise<{ message: string; updated_logs: ExerciseLog[] }> {
    try {
      const response = await this.client.put(`/workouts/${workoutId}/clients/${clientId}/exercise-order`, orderUpdates);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с планировщиком (только для администраторов)
  async getSchedulerStatus(): Promise<{
    status: {
      is_running: boolean;
      next_run: string | null;
      jobs_count: number;
    };
  }> {
    try {
      const response = await this.client.get('/scheduler/status');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async startScheduler(): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/scheduler/start');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async stopScheduler(): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/scheduler/stop');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async testNotification(): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/scheduler/test');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с настройками уведомлений
  async getNotificationSettings(): Promise<{
    notifications_enabled: boolean;
    notification_time: string;
  }> {
    try {
      const response = await this.client.get('/settings/notifications');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async saveNotificationSettings(settings: {
    notifications_enabled: boolean;
    notification_time: string;
  }): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/settings/notifications', settings);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы с часовым поясом
  async getTimezoneSettings(): Promise<{ timezone: string }> {
    try {
      const response = await this.client.get<{ timezone: string }>('/settings/timezone');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async saveTimezoneSettings(settings: { timezone: string }): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/settings/timezone', settings);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Методы для работы со всеми настройками
  async getAllTrainerSettings(): Promise<{
    default_sets: number;
    default_reps: number;
    default_weight: number;
    default_rest_time: number;
    notifications_enabled: boolean;
    notification_time: string;
    timezone: string;
  }> {
    try {
      const response = await this.client.get('/settings/all');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async saveAllTrainerSettings(settings: {
    default_sets?: number;
    default_reps?: number;
    default_weight?: number;
    default_rest_time?: number;
    notifications_enabled?: boolean;
    notification_time?: string;
    timezone?: string;
  }): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/settings/all', settings);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Настройки очистки логов (только для админов) ---
  async getLogsCleanupSettings(): Promise<LogsCleanupSettings> {
    try {
      const response = await this.client.get<LogsCleanupSettings>('/admin/logs-cleanup-settings');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateLogsCleanupSettings(settings: LogsCleanupSettingsUpdate): Promise<{ message: string; settings: LogsCleanupSettings }> {
    try {
      const response = await this.client.post<{ message: string; settings: LogsCleanupSettings }>('/admin/logs-cleanup-settings', settings);
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async runLogsCleanup(): Promise<{ 
    message: string; 
    deleted_action_logs: number; 
    cutoff_date: string 
  }> {
    try {
      const response = await this.client.post<{ 
        message: string; 
        deleted_action_logs: number; 
        cutoff_date: string 
      }>('/admin/logs-cleanup/run');
      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error);
    }
  }
}

// Экспортируем единственный экземпляр
export const apiService = new ApiService();
export default apiService; 